import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/table/data-table";
import { InviteModal, SuccessModal } from "../../components/modal";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import { calculateLevelCompensation } from "../../utils/compensation";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

const LEVELS_CACHE_KEY = "cehr_company_levels_v1";
const EMPTY_COMPENSATION = {
  basicSalary: "",
  housingAllowance: "",
  transportAllowance: "",
  otherAllowance: "",
  recurringDeduction: "",
  taxRate: "",
  pensionRate: "",
};
const COMPENSATION_FIELDS = [
  ["basicSalary", "Monthly basic salary"],
  ["housingAllowance", "Housing allowance"],
  ["transportAllowance", "Transport allowance"],
  ["otherAllowance", "Other allowance"],
  ["recurringDeduction", "Recurring deduction"],
  ["taxRate", "Tax rate (%)"],
  ["pensionRate", "Pension rate (%)"],
];
const formatSalary = (value) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 2 }).format(Number(value || 0));
const withTotalMonthlyPay = (level) => ({
  ...level,
  salary: formatSalary(calculateLevelCompensation(level).grossPay),
});

const Levels = () => {
  const cachedData = useMemo(
    () => readSessionCache(LEVELS_CACHE_KEY, { levels: [] }),
    [],
  );
  const [openlevelModal, setOpenLevelModal] = useState(false);
  const [opensucesssModal, setOpensuccessModal] = useState(false);
  const [levels, setLevels] = useState(cachedData?.levels || []);
  const [levelName, setLevelName] = useState("");
  const [careerFocus, setCareerFocus] = useState("");
  const [careerResponsibilities, setCareerResponsibilities] = useState("");
  const [showSalaryOnDashboard, setShowSalaryOnDashboard] = useState(false);
  const [compensation, setCompensation] = useState(EMPTY_COMPENSATION);
  const [additionalAllowances, setAdditionalAllowances] = useState([]);
  const [isLoading, setIsLoading] = useState(!cachedData?.levels?.length);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const openModal = () => setOpenLevelModal(true);
  const apiBaseUrl = getApiBaseUrl();

  const loadLevels = useCallback(
    async ({ silent = false } = {}) => {
      if (!apiBaseUrl) {
        setErrorMessage("API base URL is not configured.");
        return;
      }

      try {
        if (!silent && levels.length === 0) {
          setIsLoading(true);
        }
        setErrorMessage("");

        const response = await axios.get(`${apiBaseUrl}/organizations/levels`);
        const rows = (response?.data?.levels || []).map(withTotalMonthlyPay);
        setLevels(rows);
        writeSessionCache(LEVELS_CACHE_KEY, { levels: rows });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message ||
          "Unable to load levels. Please try again.";
        setErrorMessage(
          Array.isArray(responseMessage)
            ? responseMessage.join(", ")
            : responseMessage,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl, levels.length],
  );

  useEffect(() => {
    loadLevels({ silent: true });
  }, [loadLevels]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadLevels({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadLevels]);

  const resetForm = () => {
    setLevelName("");
    setCareerFocus("");
    setCareerResponsibilities("");
    setShowSalaryOnDashboard(false);
    setCompensation(EMPTY_COMPENSATION);
    setAdditionalAllowances([]);
    setErrorMessage("");
  };

  const closeLevelModal = () => {
    setOpenLevelModal(false);
    resetForm();
  };

  const createHandleSuccess = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    if (
      !levelName.trim() ||
      !careerResponsibilities.trim() ||
      compensation.basicSalary === ""
    ) {
      setErrorMessage(
        "Level name, focus & responsibility, and monthly basic salary are required.",
      );
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      await axios.post(`${apiBaseUrl}/organizations/levels`, {
        levelName: levelName.trim(),
        levelDescription: careerResponsibilities.trim(),
        careerRole: levelName.trim(),
        careerFocus: careerFocus.trim(),
        careerResponsibilities: careerResponsibilities.trim(),
        showSalaryOnDashboard,
        salaryStructure: String(Number(compensation.basicSalary || 0)),
        ...Object.fromEntries(
          COMPENSATION_FIELDS.map(([key]) => [key, Number(compensation[key] || 0)]),
        ),
        additionalAllowances: additionalAllowances
          .map((allowance) => ({ name: allowance.name.trim(), amount: Number(allowance.amount || 0) }))
          .filter((allowance) => allowance.name),
      });

      closeLevelModal();
      setOpensuccessModal(true);
      await loadLevels();
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to create level. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const moveLevel = async (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= levels.length || isReordering) return;

    const previousLevels = levels;
    const nextLevels = [...levels];
    [nextLevels[index], nextLevels[nextIndex]] = [nextLevels[nextIndex], nextLevels[index]];
    setLevels(nextLevels);
    writeSessionCache(LEVELS_CACHE_KEY, { levels: nextLevels });

    try {
      setIsReordering(true);
      const response = await axios.patch(`${apiBaseUrl}/organizations/levels/reorder`, {
        levelIds: nextLevels.map((level) => level.id),
      });
      const savedLevels = (response?.data?.levels || nextLevels).map(withTotalMonthlyPay);
      setLevels(savedLevels);
      writeSessionCache(LEVELS_CACHE_KEY, { levels: savedLevels });
    } catch (error) {
      setLevels(previousLevels);
      writeSessionCache(LEVELS_CACHE_KEY, { levels: previousLevels });
      setErrorMessage(error?.response?.data?.message || "Unable to save the level order. Please try again.");
    } finally {
      setIsReordering(false);
    }
  };

  let navigate = useNavigate();

  const headings = [
    "Level Name",
    "Level Description",
    "Total Monthly Pay",
    "Actions",
  ];
  const columns = ["levelName", "levelDescription", "salary"];
  return (
    <div className="dashboard-enter p-6">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Levels" },
        ]}
        title="Levels"
        subtitle="Define level structure and compensation tiers for your workforce."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={
          <button
            onClick={openModal}
            className="bg-[#1234CD] px-6 py-2 rounded-xl text-white"
          >
            Add new
          </button>
        }
      />

      <DataTable
        title=""
        headings={headings}
        columns={columns}
        data={levels}
        showEmptyState={!isLoading}
        emptyMessage="No levels found. Create one to get started."
        tableClassName="min-w-[900px]"
        columnClassNames={[
          "w-[18%] break-words",
          "w-[52%] whitespace-normal break-words leading-6",
          "w-[20%] whitespace-nowrap text-right",
          "w-[10%] whitespace-nowrap text-center",
        ]}
        onView={(row) => {
          sessionStorage.setItem("selectedLevelId", row.id);
          navigate(`/company-settings/levelview-details/${encodeURIComponent(row.id)}`, {
            state: { levelId: row.id },
          });
        }}
      />

      {levels.length > 1 ? (
        <section className="mt-6 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div><h2 className="font-semibold text-[#071C50]">Level order</h2><p className="mt-1 text-sm text-slate-500">Set the career progression order. Changes are saved immediately.</p></div>
            {isReordering ? <span className="text-xs font-medium text-[#1234CD]">Saving order…</span> : null}
          </div>
          <ol className="mt-4 space-y-2">
            {levels.map((level, index) => (
              <li key={level.id} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-xs font-bold text-[#1234CD]">{index + 1}</span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#071C50]">{level.levelName}</span>
                <div className="flex gap-1"><button type="button" onClick={() => moveLevel(index, -1)} disabled={index === 0 || isReordering} aria-label={`Move ${level.levelName} up`} className="rounded-lg p-2 text-[#1234CD] hover:bg-white disabled:opacity-30"><FiChevronUp /></button><button type="button" onClick={() => moveLevel(index, 1)} disabled={index === levels.length - 1 || isReordering} aria-label={`Move ${level.levelName} down`} className="rounded-lg p-2 text-[#1234CD] hover:bg-white disabled:opacity-30"><FiChevronDown /></button></div>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      <InviteModal
        isOpen={openlevelModal}
        onClose={closeLevelModal}
      >
        <div className="my-8">
          <div>
            <p className=" text-2xl font-semibold text-black font-Nunito">
              Create Level
            </p>
          </div>
          <form
            className="my-12"
            onSubmit={createHandleSuccess}
          >
            {errorMessage && (
              <p
                className="mb-4 text-sm text-red-600 dashboard-fade-in"
                role="alert"
              >
                {errorMessage}
              </p>
            )}

            <div>
              <label
                htmlFor="invite-email"
                className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-Nunito"
              >
                Level Name
              </label>
              <input
                type="text"
                id="text"
                placeholder="Title"
                value={levelName}
                onChange={(ev) => setLevelName(ev.target.value)}
                className="w-full p-2 text-center mb-2  rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>

            <div className="mt-5 border-t border-slate-100 pt-5">
              <h3 className="font-semibold text-[#071C50]">Employee career preview</h3>
              <p className="mt-1 text-xs text-slate-500">Level name is used consistently across the employee career profile. Focus and responsibility is the level description shown across the system.</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-medium text-slate-600">Level name<input readOnly value={levelName} placeholder="Uses level name" className="mt-2 w-full cursor-not-allowed rounded-xl border-2 border-[#F3F5F5] bg-slate-50 px-3 py-3 text-slate-500 outline-none" /></label>
                <label className="text-sm font-medium text-slate-600">Focus<input maxLength={160} value={careerFocus} onChange={(event) => setCareerFocus(event.target.value)} placeholder="e.g. Learn" className="mt-2 w-full rounded-xl border-2 border-[#F3F5F5] px-3 py-3 outline-none focus:border-[#1234CD]" /></label>
                <label className="text-sm font-medium text-slate-600 sm:col-span-2">Focus &amp; responsibility<textarea required maxLength={2000} rows={5} value={careerResponsibilities} onChange={(event) => setCareerResponsibilities(event.target.value)} placeholder="Describe the focus and responsibilities for this level" className="mt-2 w-full resize-y rounded-xl border-2 border-[#F3F5F5] px-3 py-3 outline-none focus:border-[#1234CD]" /></label>
              </div>
              <label className="mt-4 flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4"><span><span className="block text-sm font-semibold text-[#071C50]">Show monthly salary to employees</span><span className="mt-1 block text-xs text-slate-500">When off, salary is hidden from the employee career preview.</span></span><input type="checkbox" checked={showSalaryOnDashboard} onChange={(event) => setShowSalaryOnDashboard(event.target.checked)} className="h-5 w-5 accent-[#1234CD]" /></label>
            </div>

            <div className="mt-5 border-t border-slate-100 pt-5">
              <h3 className="font-semibold text-[#071C50]">Payroll defaults</h3>
              <p className="mt-1 text-xs text-slate-500">
                Employees assigned to this level inherit these values unless payroll overrides them individually.
              </p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {COMPENSATION_FIELDS.map(([key, label]) => (
                  <label key={key} className="text-sm font-medium text-slate-600">
                    {label}
                    <input
                      required={key === "basicSalary"}
                      type="number"
                      min="0"
                      max={key === "taxRate" || key === "pensionRate" ? "100" : undefined}
                      step="0.01"
                      value={compensation[key]}
                      onChange={(event) =>
                        setCompensation((current) => ({ ...current, [key]: event.target.value }))
                      }
                      className="mt-2 w-full rounded-xl border-2 border-[#F3F5F5] px-3 py-3 outline-none focus:border-[#1234CD]"
                    />
                  </label>
                ))}
              </div>
              <div className="mt-5 rounded-xl border border-slate-100 bg-slate-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div><h4 className="text-sm font-semibold text-[#071C50]">Additional allowances</h4><p className="text-xs text-slate-500">Add any level-specific allowance not covered above.</p></div>
                  <button type="button" onClick={() => setAdditionalAllowances((items) => [...items, { name: "", amount: "" }])} className="shrink-0 rounded-lg bg-[#1234CD] px-3 py-2 text-xs font-semibold text-white">+ Add allowance</button>
                </div>
                <div className="mt-3 space-y-3">
                  {additionalAllowances.map((allowance, index) => (
                    <div key={index} className="grid gap-2 sm:grid-cols-[1fr_160px_auto]">
                      <input required placeholder="Allowance name" value={allowance.name} onChange={(event) => setAdditionalAllowances((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item))} className="rounded-xl border-2 border-white px-3 py-3 outline-none focus:border-[#1234CD]" />
                      <input required type="number" min="0" step="0.01" placeholder="Amount" value={allowance.amount} onChange={(event) => setAdditionalAllowances((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, amount: event.target.value } : item))} className="rounded-xl border-2 border-white px-3 py-3 outline-none focus:border-[#1234CD]" />
                      <button type="button" aria-label={`Remove ${allowance.name || "allowance"}`} onClick={() => setAdditionalAllowances((items) => items.filter((_, itemIndex) => itemIndex !== index))} className="rounded-xl px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">Remove</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className=" flex justify-center mt-8 items-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="font-[700] font-Nunito  leading-[21.6px] w-40 rounded-xl text-white py-[16px] bg-[#1234CD]  h-[40px]text-[16px] disabled:opacity-60 disabled:cursor-not-allowed dashboard-button-hover"
              >
                <span>{isSubmitting ? "Creating..." : "Create"}</span>
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={opensucesssModal}
        onClose={() => setOpensuccessModal(false)}
      />

      {isLoading && levels.length === 0 ? (
        <FetchLoadingState message="Loading levels..." />
      ) : null}
    </div>
  );
};

export default Levels;
